import type { ApiClient, AuthTokens, components } from '@dorixona/api'
import { parseTokens } from '@dorixona/api'
import type { Gender } from './model'

type OtpChallengeDto = components['schemas']['OTPChallenge']
type VerifyResultDto = components['schemas']['VerifyResult']
type CustomerDto = components['schemas']['Customer']

/** Профиль из ответа сервера: `GET /me` и всё, что возвращает покупателя. */
export function toCustomer(dto: CustomerDto): Customer {
  return {
    id: dto.id ?? 0,
    firstName: dto.first_name ?? '',
    lastName: dto.last_name ?? '',
    phone: dto.phone ?? '',
    balance: dto.balance ?? 0,
    gender: dto.gender ?? '',
    birthDate: dto.birth_date ?? '',
    language: dto.language ?? 'ru',
    notifyPush: dto.notify_push ?? false,
  }
}

export interface OtpChallenge {
  challengeId: string
  /** Код живёт столько секунд (R-02). */
  expiresIn: number
  /** Новый код можно запросить через столько секунд. */
  resendIn: number
}

export type VerifyResult =
  | { status: 'ok'; tokens: AuthTokens }
  | { status: 'registration_required'; registrationToken: string }

/**
 * Ответ входа мини-аппа (`POST /auth/telegram`, BR-39): вошли; номер известен
 * из бота, осталось зарегистрироваться; или номер ещё нужен.
 */
export type TelegramResult =
  | { status: 'ok'; tokens: AuthTokens }
  | { status: 'registration_required'; registrationToken: string; firstName: string }
  | { status: 'phone_required'; telegramToken: string; firstName: string }

interface TelegramResultDto {
  status?: 'ok' | 'registration_required' | 'phone_required'
  tokens?: unknown
  telegram_token?: string
  registration_token?: string
  first_name?: string
}

function toVerifyResult(dto: VerifyResultDto): VerifyResult {
  if (dto.status === 'ok') return { status: 'ok', tokens: parseTokens(dto.tokens) }
  return { status: 'registration_required', registrationToken: dto.registration_token ?? '' }
}

export interface RegistrationInput {
  registrationToken: string
  firstName: string
  lastName: string
  gender: Gender
  /** Пустая строка означает «не указана». */
  birthDate: string
}

export interface Customer {
  id: number
  firstName: string
  lastName: string
  phone: string
  /** Баллы лояльности, 1 балл = 1 сум. */
  balance: number
  gender: 'male' | 'female' | ''
  /** День рождения `YYYY-MM-DD`; пустая строка — не указан (R-56). */
  birthDate: string
  /** API понимает три языка (ru, uz, en); интерфейс приложения пока на двух. */
  language: 'ru' | 'uz' | 'en'
  /** Переключатель push-уведомлений из профиля; СМС — только код входа (R-116). */
  notifyPush: boolean
}

export interface AuthApi {
  requestOtp(phone: string): Promise<OtpChallenge>
  verifyOtp(challengeId: string, code: string): Promise<VerifyResult>
  register(input: RegistrationInput): Promise<AuthTokens>
  me(): Promise<Customer>
  logout(refreshToken: string): Promise<void>
  /** Вход по `initData` мини-аппа — строка уходит без изменений: подпись по ней. */
  telegramLogin(initData: string): Promise<TelegramResult>
  /** Код на номер, который мини-апп спросил у покупателя (шаг `phone_required`). */
  telegramOtp(telegramToken: string, phone: string): Promise<OtpChallenge>
  /** Проверить код и привязать Telegram: следующий вход будет без экранов. */
  telegramVerify(telegramToken: string, challengeId: string, code: string): Promise<VerifyResult>
  /** Push: запомнить телефон за вошедшим покупателем (`POST /push/tokens`, BR-29). */
  registerPushToken(token: string, platform: 'android' | 'ios'): Promise<void>
  /**
   * Забыть телефон при выходе (`DELETE /push/tokens`). Токен доступа передаём
   * явно: к этому моменту сессию в приложении уже сбросили, а сервер без него
   * телефон не отвяжет — и он получал бы уведомления прежнего покупателя.
   */
  unregisterPushToken(token: string, accessToken: string): Promise<void>
}

function toChallenge(dto: OtpChallengeDto): OtpChallenge {
  return { challengeId: dto.challenge_id, expiresIn: dto.expires_in, resendIn: dto.resend_in }
}

export function createAuthApi(api: ApiClient): AuthApi {
  return {
    async requestOtp(phone) {
      return toChallenge(
        await api.post<OtpChallengeDto>('/auth/otp/request', { phone }, { auth: false }),
      )
    },

    async verifyOtp(challengeId, code) {
      return toVerifyResult(
        await api.post<VerifyResultDto>(
          '/auth/otp/verify',
          { challenge_id: challengeId, code },
          { auth: false },
        ),
      )
    },

    async register(input) {
      const tokens = await api.post<unknown>(
        '/auth/register',
        {
          registration_token: input.registrationToken,
          first_name: input.firstName,
          last_name: input.lastName,
          gender: input.gender,
          // Пустую дату не отправляем: поле необязательное.
          ...(input.birthDate ? { birth_date: input.birthDate } : {}),
        },
        { auth: false },
      )
      return parseTokens(tokens)
    },

    async me() {
      return toCustomer(await api.get<CustomerDto>('/me'))
    },

    async logout(refreshToken) {
      await api.post('/auth/logout', { refresh_token: refreshToken }, { auth: false })
    },

    async registerPushToken(token, platform) {
      await api.post('/push/tokens', { token, platform })
    },

    async unregisterPushToken(token, accessToken) {
      await api.del('/push/tokens', {
        body: { token },
        auth: false,
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    },

    async telegramLogin(initData) {
      const dto = await api.post<TelegramResultDto>(
        '/auth/telegram',
        { init_data: initData },
        { auth: false },
      )
      const firstName = dto.first_name ?? ''
      if (dto.status === 'ok') return { status: 'ok', tokens: parseTokens(dto.tokens) }
      if (dto.status === 'registration_required') {
        return {
          status: 'registration_required',
          registrationToken: dto.registration_token ?? '',
          firstName,
        }
      }
      return { status: 'phone_required', telegramToken: dto.telegram_token ?? '', firstName }
    },

    async telegramOtp(telegramToken, phone) {
      return toChallenge(
        await api.post<OtpChallengeDto>(
          '/auth/telegram/otp',
          { telegram_token: telegramToken, phone },
          { auth: false },
        ),
      )
    },

    async telegramVerify(telegramToken, challengeId, code) {
      return toVerifyResult(
        await api.post<VerifyResultDto>(
          '/auth/telegram/verify',
          { telegram_token: telegramToken, challenge_id: challengeId, code },
          { auth: false },
        ),
      )
    },
  }
}
